import { useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Loader2, Trash2, Upload } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { photoLayoutFor } from '@/pages/public/booking-page/helpers';
import type { BookingPagePhoto, PhotoLayout } from '@/pages/public/booking-page/types';
import { SettingsCard } from './primitives';

const TYPES = ['image/jpeg', 'image/png', 'image/webp'];

function LayoutIcon({ kind }: { kind: PhotoLayout }) {
  return (
    <svg width="52" height="36" viewBox="0 0 52 36" aria-hidden="true" className="shrink-0">
      <rect x="0.5" y="0.5" width="51" height="35" rx="5" fill="#fff" stroke="var(--line-strong)" />
      {kind === 'none' && (<><rect x="5" y="5" width="42" height="12" rx="3" fill="var(--brand-100)" /><rect x="9" y="8" width="6" height="6" rx="1.5" fill="var(--brand-500)" /></>)}
      {kind === 'cover' && <rect x="5" y="5" width="42" height="14" rx="3" fill="var(--text-muted, #94a3b8)" />}
      {kind === 'gallery' && (<><rect x="5" y="5" width="26" height="16" rx="3" fill="#94a3b8" /><rect x="33" y="5" width="14" height="7" rx="2" fill="#94a3b8" /><rect x="33" y="14" width="14" height="7" rx="2" fill="#94a3b8" /></>)}
      <rect x="5" y="25" width="30" height="3" rx="1.5" fill="var(--line-strong)" />
    </svg>
  );
}

interface PhotosCardProps {
  photos: BookingPagePhoto[];
  max: number;
  maxBytes: number;
  onUpload: (file: File) => Promise<void>;
  onReorder: (ids: string[]) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export function PhotosCard({ photos, max, maxBytes, onUpload, onReorder, onDelete }: PhotosCardProps) {
  const { t } = useTranslation('adminBookingPage');
  const { toast } = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const [dragId, setDragId] = useState<string | null>(null);
  const layout = photoLayoutFor(photos.length);

  const pick = async (files: FileList | null) => {
    if (!files) return;
    let room = max - photos.length;
    for (const file of Array.from(files)) {
      if (room <= 0) { toast({ title: t('photos.limit', { max }), variant: 'destructive' }); break; }
      if (!TYPES.includes(file.type)) { toast({ title: t('photos.wrongType', { name: file.name }), variant: 'destructive' }); continue; }
      if (file.size > maxBytes) { toast({ title: t('photos.tooBig', { name: file.name }), variant: 'destructive' }); continue; }
      room -= 1;
      setUploading((n) => n + 1);
      try { await onUpload(file); } finally { setUploading((n) => n - 1); }
    }
    if (input.current) input.current.value = '';
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= photos.length) return;
    const ids = photos.map((p) => p.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    void onReorder(ids);
  };

  const drop = (e: DragEvent, overId: string) => {
    e.preventDefault();
    if (!dragId || dragId === overId) return;
    const ids = photos.map((p) => p.id).filter((id) => id !== dragId);
    ids.splice(ids.indexOf(overId), 0, dragId);
    setDragId(null);
    void onReorder(ids);
  };

  const tiles: { kind: PhotoLayout; title: string; hint: string }[] = [
    { kind: 'none', title: t('photos.layoutNone'), hint: t('photos.layoutNoneHint') },
    { kind: 'cover', title: t('photos.layoutCover'), hint: t('photos.layoutCoverHint') },
    { kind: 'gallery', title: t('photos.layoutGallery'), hint: t('photos.layoutGalleryHint') },
  ];

  return (
    <SettingsCard title={t('photos.title')} description={t('photos.description')} testId="photos-card">
      <div className="grid grid-cols-3 gap-2">
        {tiles.map((tile) => (
          <div
            key={tile.kind}
            className={cn('flex flex-col items-start gap-2 rounded-xl p-3 sm:flex-row sm:items-center sm:gap-3', tile.kind === layout ? 'border-[1.5px] border-brand-500 bg-brand-50' : 'border border-line bg-surface')}
            data-testid={`layout-${tile.kind}`}
            data-active={tile.kind === layout}
          >
            <LayoutIcon kind={tile.kind} />
            <div className="flex flex-col">
              <span className={cn('text-[13px] font-semibold', tile.kind === layout ? 'text-brand-700' : 'text-ink')}>{tile.title}</span>
              <span className="text-[12px] text-soft">{tile.hint}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Two columns on a phone: three left each tile ~100px wide, which is less
          than the three 44px controls inside it need, so every button was
          squashed to 25px and became impossible to hit with a thumb. */}
      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-5 sm:gap-2.5" data-testid="photo-grid">
        {photos.map((photo, index) => (
          <li
            key={photo.id}
            draggable
            onDragStart={() => setDragId(photo.id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => drop(e, photo.id)}
            className={cn('group relative h-32 overflow-hidden rounded-lg border border-line sm:h-28', dragId === photo.id && 'opacity-50')}
            data-testid="admin-photo"
            data-photo-id={photo.id}
          >
            <img src={photo.thumb_url || ''} alt={photo.alt || ''} className="h-full w-full object-cover" draggable={false} />
            {index === 0 && (
              <span className="absolute left-1.5 top-1.5 rounded-pill bg-surface px-2 py-0.5 text-[11px] font-semibold text-ink">{t('photos.cover')}</span>
            )}
            {/* Remove sits in its own corner rather than in a row with the
                arrows: sharing one row is what let them squeeze each other. */}
            <button
              type="button"
              aria-label={t('photos.remove')}
              onClick={() => onDelete(photo.id)}
              className="absolute right-1 top-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-surface/95 text-danger-text shadow-sm sm:h-9 sm:w-9"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <div className="absolute inset-x-1 bottom-1 flex gap-1">
              <button type="button" aria-label={t('photos.moveEarlier')} disabled={index === 0} onClick={() => move(index, -1)} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-surface/95 text-ink shadow-sm disabled:opacity-40 sm:h-9 sm:w-9">
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button type="button" aria-label={t('photos.moveLater')} disabled={index === photos.length - 1} onClick={() => move(index, 1)} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-surface/95 text-ink shadow-sm disabled:opacity-40 sm:h-9 sm:w-9">
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </li>
        ))}
        {photos.length < max && (
          <li>
            <button
              type="button"
              onClick={() => input.current?.click()}
              className="flex h-24 w-full flex-col items-center justify-center gap-1 rounded-lg border-[1.5px] border-dashed border-line-strong bg-bg-public text-[13px] font-semibold text-brand-500 hover:border-brand-300 sm:h-28"
              data-testid="add-photos"
            >
              {uploading > 0 ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
              {uploading > 0 ? t('photos.uploading') : t('photos.add')}
              <span className="hidden text-[11px] font-normal text-soft sm:block">{t('photos.addHint')}</span>
            </button>
          </li>
        )}
      </ul>
      <input
        ref={input}
        type="file"
        accept={TYPES.join(',')}
        multiple
        className="sr-only"
        data-testid="photo-input"
        onChange={(e) => pick(e.target.files)}
      />
      <div className="flex flex-wrap justify-between gap-2 text-body-sm text-soft">
        <span>{t('photos.reorderHint')}</span>
        <span className="tabular-nums" data-testid="photo-count">{t('photos.count', { count: photos.length, max })}</span>
      </div>
    </SettingsCard>
  );
}
